// Nurse1Page.tsx
import React, { useMemo, useState, useEffect, JSX } from "react";
import api from "../../../api/axiosConfig";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../../../components/Center Admin/layout/AppLayout";
import "../../../center admin css/Nurse2Page.css";
import "../../../center admin css/centerAdmin.css";
import Nurse1ActivePatientCard from "../../../components/Center Admin/Reuse/Nurse1ActivePatientCard";

import { AnthropometricMeasurementForm } from "../../../components/Center Admin/forms/AnthropometricMeasurementForm";
import { PhysicalExaminationForm } from "../../../components/Center Admin/forms/PhysicalExaminationForm";
import { ADMinorSymptomsExaminationForm } from "../../../components/Center Admin/forms/ADMinorSymptomsExaminationForm";
import { BUSampleForm } from "../../../components/Center Admin/forms/BUSampleForm";
import BnuSampleForm from "../../../components/Center Admin/forms/sentToLab";
import LabReportForm from "../../../components/Center Admin/forms/LabReportForm";
import { VisionScreeningForm } from "../../../components/Center Admin/forms/VisionScreeningForm";
import { OralImagingForm } from "../../../components/Center Admin/forms/OralImagingForm";
import { VitalsExaminationForm } from "../../../components/Center Admin/forms/VitalsExaminationForm";
import { SalivaSampleForm } from "../../../components/Center Admin/forms/SalivaSampleForm";
import { CariogramInputForm } from "../../../components/Center Admin/forms/CariogramInputForm";
import AudiologyScreeningForm from "../../../components/Center Admin/forms/AudiologyScreeningForm";

/* ---------------- helpers ---------------- */
const normalize = (s?: string) =>
  (s ?? "").toString().trim().toUpperCase().replace(/\s+/g, " ");

type BackendForm = {
  _id?: string;
  name?: string;
  key?: string;
  category?: string;
  isActive?: boolean;
};
type AllowedForm = {
  id: string;
  label: string;
  render: (emrId: string) => JSX.Element;
};

/** Robust reader: finds the user object in storage across keys + stores */
function loadUserFromStorage(): any | null {
  const keys = [
    "currentUser",
    "CurrrentResponse",
    "CurrentResponse",
    "backendResponse",
  ];
  const stores: Storage[] = [sessionStorage, localStorage];

  const read = (store: Storage, key: string) => {
    try {
      const raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  for (const s of stores) {
    for (const k of keys) {
      const val = read(s, k);
      if (val && typeof val === "object") return val;
    }
  }
  return null;
}

/* ---------------- page ---------------- */
export const Nurse1Page: React.FC = () => {
  const [activeView] = useState<"forms" | "queue">("forms");

  const { activePatient } = useOutletContext<any>();

  const emrId = activePatient?.child?.childId || "";

  const canCollectBUSample =
    activePatient?.canCollectBUSample ??
    activePatient?.meta?.canCollectBUSample ??
    false;

  console.log(
    "activePatient:",
    activePatient,
    "canCollectBUSample:",
    canCollectBUSample,
  );

  const [loadingForms, setLoadingForms] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [visibleForms, setVisibleForms] = useState<AllowedForm[]>([]);
  const [showADForm, setShowADForm] = useState<boolean>(false);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [isStarted, setIsStarted] = useState(false);
  const [formStatus, setFormStatus] = useState({
    anthropometry: false,
    physical: false,
    adMinor: false,
    buSample: false,
    physicalSymptomsObserved: false,
  });

  const updateFormStatus = (form: string, status: boolean) => {
    setFormStatus((prev) => ({
      ...prev,
      [form]: status,
    }));
  };

  const updatePhysicalSymptomsObserved = (status: boolean) => {
    setFormStatus((prev) => ({
      ...prev,
      physicalSymptomsObserved: status,
    }));
  };

  const FORM_COMPONENTS: Record<string, (emrId: string) => JSX.Element> = {
    "ANTHROPOMETRIC MEASUREMENT": (emrId) => (
      <AnthropometricMeasurementForm
        emrId={emrId}
        onComplete={() => updateFormStatus("anthropometry", true)}
      />
    ),

    "PHYSICAL EXAMINATION": (emrId) => (
      <PhysicalExaminationForm
        emrId={emrId}
        onComplete={() => updateFormStatus("physical", true)}
        onSymptomsObservedChange={updatePhysicalSymptomsObserved}
      />
    ),

    "AD MINOR SYMPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm
        emrId={emrId}
        onComplete={() => updateFormStatus("adMinor", true)}
      />
    ),

    "ADMINOR SYMPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm
        emrId={emrId}
        onComplete={() => updateFormStatus("adMinor", true)}
      />
    ),

    "ADMINOR SYSPTOMS EXAMINATION": (emrId) => (
      <ADMinorSymptomsExaminationForm
        emrId={emrId}
        onComplete={() => updateFormStatus("adMinor", true)}
      />
    ),

    "BLOOD AND URIN SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),
    "BLOOD AND URINE SAMPLE COLLECTED": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),
    "BLOOD SAMPLE": (emrId) => (
      <BUSampleForm
        emrId={emrId}
        onComplete={() => updateFormStatus("buSample", true)}
      />
    ),

    "BLOOD SAMPLE SENT TO LAB": (emrId) => <BnuSampleForm emrId={emrId} />,
    "LAB REPORT": (emrId) => <LabReportForm emrId={emrId} />,
    "VISION SCREENING": (emrId) => <VisionScreeningForm emrId={emrId} />,
    "ORAL IMAGING": (emrId) => <OralImagingForm emrId={emrId} />,
    "VITALS EXAMINATION": (emrId) => <VitalsExaminationForm emrId={emrId} />,
    "CARIOGRAM SALAIVA SAMPLE": (emrId) => <SalivaSampleForm emrId={emrId} />,
    "CARIOGRAM INPUT": (emrId) => <CariogramInputForm emrId={emrId} />,
    "AUDIOLOGY SCREENING": (emrId) => <AudiologyScreeningForm emrId={emrId} />,
  };

  useEffect(() => {
    const checkADCondition = async () => {
      if (!emrId) {
        console.log("EMR ID not available yet");
        return;
      }
      try {
        console.log("EMR ID =", emrId);
        console.log("REQUEST URL =", `/api/scoring/scoresId/${emrId}/health`);

        const { data } = await api.Questionnair.get(
          `/api/scoring/scoresId/${emrId}/health`,
        );
        console.log("FULL RESPONSE =", data);
        const answer = data?.scores?.resultsById?.["1220"];
        // const answer = data?.["1220"];

        console.log("1220 Answer =", answer);
        console.log("Uppercase =", String(answer).toUpperCase());
        console.log(
          "Condition Result =",
          String(answer).toUpperCase() === "YES" ||
            String(answer).toUpperCase() === "MAYBE",
        );

        if (
          String(answer).toUpperCase() === "YES" ||
          String(answer).toUpperCase() === "MAYBE"
        ) {
          console.log("SETTING TRUE");
          setShowADForm(true);
        } else {
          console.log("SETTING FALSE");
          setShowADForm(false);
        }
      } catch (err) {
        console.error("Failed to fetch 1220 condition", err);
        setShowADForm(false); // safe fallback
      }
    };

    checkADCondition();
  }, [emrId]);

  const validateBeforeComplete = () => {
    if (!formStatus.anthropometry) {
      alert("Please complete Anthropometric Measurement form");
      return false;
    }
    if (!formStatus.physical) {
      alert("Please complete Physical Examination form");
      return false;
    }
    if (showADForm && !formStatus.adMinor) {
      alert("Please complete AD Minor Symptoms form");
      return false;
    }
    return true;
  };

  useEffect(() => {
    setLoadingForms(true);
    setLoadError(null);

    try {
      const user = loadUserFromStorage();
      if (!user) {
        throw new Error("User data not found in session. Please log in again.");
      }

      // Prefer role-based forms; fallback to flat forms list
      const fromRole: BackendForm[] =
        user?.roleAssignments?.[0]?.roleId?.formIds ?? [];
      const fallback: BackendForm[] = user?.forms ?? [];
      const backendForms: BackendForm[] =
        (fromRole?.length ? fromRole : fallback)?.filter(
          (f) => f?.isActive !== false,
        ) ?? [];

      const built: AllowedForm[] = [];
      const seen = new Set<string>();
      backendForms.forEach((bf) => {
        const nameN = normalize(bf?.name);
        const keyN = normalize(bf?.key);

        console.log("FORM NAME =", bf?.name);
        console.log("FORM KEY =", bf?.key);
        console.log("NORMALIZED NAME =", nameN);
        console.log("NORMALIZED KEY =", keyN);

        const isBUSampleForm =
          nameN.includes("BLOOD AND URINE SAMPLE") ||
          nameN.includes("BLOOD AND URIN SAMPLE") ||
          keyN.includes("BLOOD AND URINE SAMPLE") ||
          keyN.includes("BLOOD AND URIN SAMPLE");

        if (isBUSampleForm && !canCollectBUSample) {
          return;
        }

        if (
          (nameN.includes("ADMINOR") ||
            keyN.includes("ADMINOR") ||
            nameN.includes("AD MINOR") ||
            keyN.includes("AD MINOR") ||
            nameN.includes("ATOPIC") ||
            keyN.includes("ATOPIC")) &&
          !showADForm
        ) {
          return;
        }

        const renderer =
          FORM_COMPONENTS[nameN] ?? FORM_COMPONENTS[keyN] ?? null;
        if (!renderer) return;
        const label = bf?.name || bf?.key || "Form";
        const unique = bf?._id || `${label}-${keyN || nameN}`;
        if (!seen.has(unique)) {
          seen.add(unique);
          built.push({ id: unique, label, render: renderer });
        }
      });

      // setVisibleForms(built);
      // setActiveIdx((prev) => (built[prev] ? prev : 0));

      // if (built.length === 0) {

      // Keep the existing backend form order,
      // but always move B&U Sample to the last tab.
      const orderedForms = [...built].sort((a, b) => {
        const aLabel = normalize(a.label);
        const bLabel = normalize(b.label);

        const isABUSample =
          aLabel.includes("B&U SAMPLE") ||
          aLabel.includes("BLOOD AND URINE SAMPLE") ||
          aLabel.includes("BLOOD AND URIN SAMPLE");

        const isBBUSample =
          bLabel.includes("B&U SAMPLE") ||
          bLabel.includes("BLOOD AND URINE SAMPLE") ||
          bLabel.includes("BLOOD AND URIN SAMPLE");

        if (isABUSample && !isBBUSample) return 1;
        if (!isABUSample && isBBUSample) return -1;

        return 0;
      });

      setVisibleForms(orderedForms);

      console.log("BUILT FORMS =", built);
      console.log("ORDERED FORMS =", orderedForms);
      console.log("showADForm =", showADForm);

      setActiveIdx((prev) => (orderedForms[prev] ? prev : 0));

      if (orderedForms.length === 0) {
        setTimeout(() => {
          window.alert(
            "No form is assigned to your role. Please contact the Super Admin.",
          );
        }, 0);
      }
    } catch (err: any) {
      console.error("Nurse1Page: failed to load or process user forms", err);
      setLoadError(err.message || "Could not load permitted forms.");
      setVisibleForms([]);
      setActiveIdx(0);
    } finally {
      setLoadingForms(false);
    }
  }, [showADForm, canCollectBUSample]);

  return (
    <section className="bnu-main">
      <Nurse1ActivePatientCard
        onPatientSelect={(patient) => console.log("Selected:", patient)}
        showActions={true}
        validateBeforeComplete={validateBeforeComplete}
        formStatus={formStatus}
        showADForm={showADForm}
        onStationStarted={() => setIsStarted(true)}
      />

      {activeView === "forms" && (
        <>
          <div className="nurse-subtabs">
            {loadingForms && (
              <div className="nurse-subtab disabled">Loading…</div>
            )}
            {!loadingForms && loadError && (
              <div className="nurse-subtab disabled">{loadError}</div>
            )}
            {!loadingForms && !loadError && visibleForms.length > 0 && (
              <>
                {visibleForms.map((f, i) => (
                  <button
                    key={f.id}
                    className={`nurse-subtab${
                      activeIdx === i ? " active" : ""
                    }`}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    title={f.label}
                  >
                    {f.label}
                  </button>
                ))}
              </>
            )}
            {!loadingForms && !loadError && visibleForms.length === 0 && (
              <div className="nurse-subtab disabled">No forms assigned</div>
            )}
          </div>

          {activePatient &&
            isStarted &&
            !loadingForms &&
            !loadError &&
            visibleForms[activeIdx] && (
              <>{visibleForms[activeIdx].render(emrId)}</>
            )}
        </>
      )}
    </section>
  );
};
